import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/common/Header';
import { addActivity } from '../utils/activityLogger';
import api from '../services/api';
import {
  Plus,
  Edit2,
  Trash2,
  CloudRain,
  Thermometer,
  Wind,
  Droplet,
  MapPin,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  X,
  Clock,
  ArrowLeft,
  Copy,
  Info,
  Eye,
  EyeOff,
  DollarSign,
  CheckCircle,
  Zap,
} from 'lucide-react';

// Type definitions
interface WeatherRule {
  id: string;
  ruleName: string; // Custom rule name
  campaignName: string;
  locations: string[]; // Changed to array for multi-city targeting
  weatherMetric: 'temperature' | 'rain' | 'wind' | 'humidity';
  comparator: '>' | '<' | '>=' | '<=' | '=';
  value: number;
  adjustmentTarget: 'bid' | 'budget';
  adjustmentType: 'increase' | 'decrease';
  adjustmentPercentage: number;
  cooldownHours: number;
  isActive: boolean;
  lastTriggered?: string;
}

interface RuleFormData {
  ruleName: string; // Custom rule name
  campaignName: string;
  locations: string[]; // Changed to array for multi-city targeting
  weatherMetric: 'temperature' | 'rain' | 'wind' | 'humidity';
  comparator: '>' | '<' | '>=' | '<=' | '=';
  value: number;
  adjustmentTarget: 'bid' | 'budget';
  adjustmentType: 'increase' | 'decrease';
  adjustmentPercentage: number;
  cooldownHours: number;
  isActive: boolean;
  ruleTemplate: 'custom' | 'rain_tiered' | 'temperature_seasonal' | 'wind_emergency' | 'complete_yearround';
  autoRevert48h: boolean;
  baselineBudget: number;
  adjustmentIntensity: number; // 50-200% (100 = normal, 50 = conservative, 200 = aggressive)
}

// Campaigns will be loaded from API

// Rules will be loaded from API
export default function Rules() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [rules, setRules] = useState<WeatherRule[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [campaignAccounts, setCampaignAccounts] = useState<any[]>([]);
  const [locations, setLocations] = useState<Array<{id: number, name: string}>>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [showInactiveCampaigns, setShowInactiveCampaigns] = useState(false); // Toggle for showing inactive campaigns
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<WeatherRule | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCooldownHelp, setShowCooldownHelp] = useState(false);
  // const [currentStep, setCurrentStep] = useState(1); // TODO Phase 3: Wizard step (1-4)

  const [formData, setFormData] = useState<RuleFormData>({
    ruleName: '', // Custom rule name
    campaignName: '',
    locations: [], // Changed to array
    weatherMetric: 'rain',
    comparator: '>=',
    value: 0,
    adjustmentTarget: 'bid',
    adjustmentType: 'increase',
    adjustmentPercentage: 15,
    cooldownHours: 2,
    isActive: true,
    ruleTemplate: 'custom',
    autoRevert48h: false,
    baselineBudget: 300,
    adjustmentIntensity: 100, // 100 = normal (1.0x multiplier)
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.get('/locations');
        if (response.data.success && response.data.locations) {
          setLocations(response.data.locations);
        }
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };

    if (isAuthenticated) {
      fetchLocations();
    }
  }, [isAuthenticated]);

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoadingCampaigns(true);
        setCampaignsError(null);
        const response = await api.get('/campaigns');

        if (response.data.success && response.data.accounts) {
          // Store accounts with campaigns (don't flatten)
          const accountsWithCampaigns = response.data.accounts.filter(
            (account: any) => account.campaigns && account.campaigns.length > 0
          );

          if (accountsWithCampaigns.length > 0) {
            // Also create flattened list for backward compatibility
            const allCampaigns: any[] = [];
            accountsWithCampaigns.forEach((account: any) => {
              allCampaigns.push(...account.campaigns);
            });
            setCampaigns(allCampaigns);
            setCampaignAccounts(accountsWithCampaigns);
          } else {
            setCampaignsError('No campaigns found. Please sync campaigns first.');
          }
        } else {
          setCampaignsError('No campaigns found. Please sync campaigns first.');
        }
      } catch (error: any) {
        console.error('Failed to fetch campaigns:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        } else {
          setCampaignsError('Failed to load campaigns. Please try syncing from the Campaigns page.');
        }
      } finally {
        setLoadingCampaigns(false);
      }
    };

    if (isAuthenticated) {
      fetchCampaigns();
    }
  }, [isAuthenticated, navigate]);

  // Fetch rules from API
  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await api.get('/rules');

        if (response.data.success && response.data.rules) {
          // Map backend rules to frontend format
          const mappedRules = response.data.rules.map((rule: any) => ({
            id: rule.id.toString(),
            ruleName: rule.rule_name || `Rule for ${rule.campaign_name}`,
            campaignName: rule.campaign_name,
            locations: rule.location_names || [], // Use array of location names from backend
            weatherMetric: rule.weather_metric === 'precip_prob' ? 'rain' : rule.weather_metric,
            comparator: rule.comparator,
            value: parseFloat(rule.threshold_value),
            adjustmentTarget: rule.adjustment_target || 'bid',
            adjustmentType: rule.bid_adjustment_type,
            adjustmentPercentage: parseFloat(rule.bid_adjustment_percent),
            cooldownHours: rule.cooldown_minutes / 60,
            isActive: rule.is_active,
            lastTriggered: rule.updated_at,
          }));
          setRules(mappedRules);
        } else {
          setRules([]);
        }
      } catch (error: any) {
        console.error('Failed to fetch rules:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        }
        setRules([]);
      }
    };

    if (isAuthenticated) {
      fetchRules();
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) return null;

  // Helper functions
  const getWeatherIcon = (metric: string) => {
    switch (metric) {
      case 'rain':
        return <CloudRain className="w-4 h-4" />;
      case 'temperature':
        return <Thermometer className="w-4 h-4" />;
      case 'wind':
        return <Wind className="w-4 h-4" />;
      case 'humidity':
        return <Droplet className="w-4 h-4" />;
      default:
        return <CloudRain className="w-4 h-4" />;
    }
  };

  const getWeatherLabel = (metric: string) => {
    switch (metric) {
      case 'rain':
        return 'Rain Probability';
      case 'temperature':
        return 'Temperature';
      case 'wind':
        return 'Wind Speed';
      case 'humidity':
        return 'Humidity';
      default:
        return metric;
    }
  };

  const getWeatherUnit = (metric: string) => {
    switch (metric) {
      case 'rain':
        return '%';
      case 'temperature':
        return '°C';
      case 'wind':
        return 'km/h';
      case 'humidity':
        return '%';
      default:
        return '';
    }
  };

  const formatCondition = (rule: WeatherRule) => {
    return `${getWeatherLabel(rule.weatherMetric)} ${rule.comparator} ${rule.value}${getWeatherUnit(rule.weatherMetric)}`;
  };

  // Modal handlers
  const openCreateModal = () => {
    setFormData({
      ruleName: '',
      campaignName: '',
      locations: [],
      weatherMetric: 'rain',
      comparator: '>=',
      value: 0,
      adjustmentTarget: 'bid',
      adjustmentType: 'increase',
      adjustmentPercentage: 15,
      cooldownHours: 2,
      isActive: true,
      ruleTemplate: 'custom',
      autoRevert48h: false,
      baselineBudget: 300,
      adjustmentIntensity: 100,
    });
    // setCurrentStep(1); // TODO Phase 3: Reset wizard step
    setIsCreateModalOpen(true);
  };

  const openEditModal = (rule: WeatherRule) => {
    setEditingRule(rule);
    setFormData({
      ruleName: rule.ruleName,
      campaignName: rule.campaignName,
      locations: rule.locations,
      weatherMetric: rule.weatherMetric,
      comparator: rule.comparator,
      value: rule.value,
      adjustmentTarget: rule.adjustmentTarget,
      adjustmentType: rule.adjustmentType,
      adjustmentPercentage: rule.adjustmentPercentage,
      cooldownHours: rule.cooldownHours,
      isActive: rule.isActive,
      ruleTemplate: 'custom',
      autoRevert48h: false,
      baselineBudget: 300,
      adjustmentIntensity: 100,
    });
    // setCurrentStep(1); // TODO Phase 3: Reset wizard step
    setIsEditModalOpen(true);
  };

  // Duplicate handler
  const handleDuplicateRule = (rule: WeatherRule) => {
    setFormData({
      ruleName: `${rule.ruleName} (Copy)`,
      campaignName: rule.campaignName,
      locations: rule.locations,
      weatherMetric: rule.weatherMetric,
      comparator: rule.comparator,
      value: rule.value,
      adjustmentTarget: rule.adjustmentTarget,
      adjustmentType: rule.adjustmentType,
      adjustmentPercentage: rule.adjustmentPercentage,
      cooldownHours: rule.cooldownHours,
      isActive: false, // Start inactive for safety
      ruleTemplate: 'custom',
      autoRevert48h: false,
      baselineBudget: 300,
      adjustmentIntensity: 100,
    });
    // setCurrentStep(1); // TODO Phase 3: Reset wizard step
    setIsCreateModalOpen(true);
  };

  const closeModals = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setEditingRule(null);
  };

  // 🎯 Template Rule Generator
  const generateTemplateRules = (
    template: string,
    baselineBudget: number,
    intensity: number, // 50-200 (percentage multiplier)
    autoRevert: boolean
  ): any[] => {
    const autoRevertSuffix = autoRevert ? ' [AUTO-REVERT-48H]' : '';
    const intensityMultiplier = intensity / 100; // Convert 100 → 1.0x, 150 → 1.5x, etc.

    switch (template) {
      case 'rain_tiered':
        return [
          // Peak Rules (Increase) - Multiplied by intensity
          { rule_name: `Storm Warning (≥90%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '>=', threshold_value: 90, adjustment_target: 'bid', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round(50 * intensityMultiplier), cooldown_hours: 0.5 },
          { rule_name: `Heavy Rain (≥70%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '>=', threshold_value: 70, adjustment_target: 'bid', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round(30 * intensityMultiplier), cooldown_hours: 0.75 },
          { rule_name: `Moderate Rain (≥50%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '>=', threshold_value: 50, adjustment_target: 'bid', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round(15 * intensityMultiplier), cooldown_hours: 1 },
          // Balance Rules (Decrease) - Multiplied by intensity
          { rule_name: `Low Rain (<20%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '<', threshold_value: 20, adjustment_target: 'bid', bid_adjustment_type: 'decrease', bid_adjustment_percent: Math.round(30 * intensityMultiplier), cooldown_hours: 4 },
          { rule_name: `Very Low Rain (<10%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '<', threshold_value: 10, adjustment_target: 'bid', bid_adjustment_type: 'decrease', bid_adjustment_percent: Math.round(50 * intensityMultiplier), cooldown_hours: 6 },
          { rule_name: `Clear Skies (<5%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '<', threshold_value: 5, adjustment_target: 'bid', bid_adjustment_type: 'decrease', bid_adjustment_percent: Math.round(70 * intensityMultiplier), cooldown_hours: 8 },
        ];

      case 'temperature_seasonal':
        return [
          // Summer Rules (Hot) - Multiplied by intensity
          { rule_name: `Extreme Heat (≥35°C)${autoRevertSuffix}`, weather_metric: 'temperature', comparator: '>=', threshold_value: 35, adjustment_target: 'bid', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round(40 * intensityMultiplier), cooldown_hours: 0.5 },
          { rule_name: `Hot Day (≥30°C)${autoRevertSuffix}`, weather_metric: 'temperature', comparator: '>=', threshold_value: 30, adjustment_target: 'bid', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round(20 * intensityMultiplier), cooldown_hours: 1 },
          // Winter Rules (Cold) - Multiplied by intensity
          { rule_name: `Freezing (<5°C)${autoRevertSuffix}`, weather_metric: 'temperature', comparator: '<', threshold_value: 5, adjustment_target: 'bid', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round(30 * intensityMultiplier), cooldown_hours: 0.5 },
          { rule_name: `Cold Day (<10°C)${autoRevertSuffix}`, weather_metric: 'temperature', comparator: '<', threshold_value: 10, adjustment_target: 'bid', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round(15 * intensityMultiplier), cooldown_hours: 1 },
        ];

      case 'wind_emergency':
        return [
          { rule_name: `Severe Wind (≥50 km/h)${autoRevertSuffix}`, weather_metric: 'wind_speed', comparator: '>=', threshold_value: 50, adjustment_target: 'bid', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round(60 * intensityMultiplier), cooldown_hours: 0.25 },
          { rule_name: `Strong Wind (≥35 km/h)${autoRevertSuffix}`, weather_metric: 'wind_speed', comparator: '>=', threshold_value: 35, adjustment_target: 'bid', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round(30 * intensityMultiplier), cooldown_hours: 0.5 },
        ];

      case 'complete_yearround':
        // Budget template: Calculate amounts based on baseline × intensity
        return [
          // Peak Event Rules (Budget Increases) - Calculated from baseline
          { rule_name: `Storm Warning (≥95%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '>=', threshold_value: 95, adjustment_target: 'budget', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round((baselineBudget * 0.5) * intensityMultiplier), cooldown_hours: 0.5 },
          { rule_name: `Heavy Rain (≥80%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '>=', threshold_value: 80, adjustment_target: 'budget', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round((baselineBudget * 0.33) * intensityMultiplier), cooldown_hours: 0.75 },
          { rule_name: `Moderate Rain (≥60%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '>=', threshold_value: 60, adjustment_target: 'budget', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round((baselineBudget * 0.17) * intensityMultiplier), cooldown_hours: 1 },
          { rule_name: `Light Rain (≥40%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '>=', threshold_value: 40, adjustment_target: 'budget', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round((baselineBudget * 0.08) * intensityMultiplier), cooldown_hours: 2 },
          // Balance Rules (Budget Decreases) - Calculated from baseline
          { rule_name: `Clear Skies (<10%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '<', threshold_value: 10, adjustment_target: 'budget', bid_adjustment_type: 'decrease', bid_adjustment_percent: Math.round((baselineBudget * 0.27) * intensityMultiplier), cooldown_hours: 4 },
          { rule_name: `Very Dry (<5%)${autoRevertSuffix}`, weather_metric: 'precip_prob', comparator: '<', threshold_value: 5, adjustment_target: 'budget', bid_adjustment_type: 'decrease', bid_adjustment_percent: Math.round((baselineBudget * 0.40) * intensityMultiplier), cooldown_hours: 6 },
          // Seasonal Baseline - Calculated from baseline
          { rule_name: `Summer Baseline Boost${autoRevertSuffix}`, weather_metric: 'temperature', comparator: '>=', threshold_value: 28, adjustment_target: 'budget', bid_adjustment_type: 'increase', bid_adjustment_percent: Math.round((baselineBudget * 0.07) * intensityMultiplier), cooldown_hours: 24 },
          { rule_name: `Winter Baseline Reduction${autoRevertSuffix}`, weather_metric: 'temperature', comparator: '<', threshold_value: 15, adjustment_target: 'budget', bid_adjustment_type: 'decrease', bid_adjustment_percent: Math.round((baselineBudget * 0.07) * intensityMultiplier), cooldown_hours: 24 },
        ];

      default:
        return [];
    }
  };

  // CRUD operations
  const handleCreateRule = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Find campaign ID from campaign name
      const campaign = campaigns.find(c => c.campaign_name === formData.campaignName);
      if (!campaign) {
        alert('Campaign not found. Please select a valid campaign.');
        return;
      }

      // Map frontend metric names to backend
      const metricMap: any = {
        'rain': 'precip_prob',
        'temperature': 'temperature',
        'wind': 'wind_speed',
        'humidity': 'humidity'
      };

      // Map location names to IDs
      const locationIds = formData.locations
        .map(locationName => {
          const location = locations.find(l => l.name === locationName);
          return location ? location.id : null;
        })
        .filter(id => id !== null);

      if (locationIds.length === 0) {
        alert('Please select at least one valid location.');
        return;
      }

      // 🎯 TEMPLATE SYSTEM: Generate multiple rules if template selected
      if (formData.ruleTemplate !== 'custom') {
        const templateRules = generateTemplateRules(
          formData.ruleTemplate,
          formData.baselineBudget,
          formData.adjustmentIntensity,
          formData.autoRevert48h
        );

        // Create all template rules in sequence
        let createdCount = 0;
        for (const templateRule of templateRules) {
          const response = await api.post('/rules', {
            campaign_id: campaign.id,
            location_ids: locationIds,
            rule_name: templateRule.rule_name,
            weather_metric: templateRule.weather_metric,
            comparator: templateRule.comparator,
            threshold_value: templateRule.threshold_value,
            adjustment_target: templateRule.adjustment_target,
            bid_adjustment_type: templateRule.bid_adjustment_type,
            bid_adjustment_percent: templateRule.bid_adjustment_percent,
            cooldown_hours: templateRule.cooldown_hours,
            is_active: formData.isActive,
          });

          if (response.data.success) {
            createdCount++;
          }
        }

        // Refresh rules list after all created
        const rulesResponse = await api.get('/rules');
        if (rulesResponse.data.success && rulesResponse.data.rules) {
          const mappedRules = rulesResponse.data.rules.map((rule: any) => ({
            id: rule.id.toString(),
            ruleName: rule.rule_name || `Rule for ${rule.campaign_name}`,
            campaignName: rule.campaign_name,
            locations: rule.location_names || [],
            weatherMetric: rule.weather_metric === 'precip_prob' ? 'rain' : rule.weather_metric,
            comparator: rule.comparator,
            value: parseFloat(rule.threshold_value),
            adjustmentTarget: rule.adjustment_target || 'bid',
            adjustmentType: rule.bid_adjustment_type,
            adjustmentPercentage: parseFloat(rule.bid_adjustment_percent),
            cooldownHours: rule.cooldown_minutes / 60,
            isActive: rule.is_active,
            lastTriggered: rule.updated_at,
          }));
          setRules(mappedRules);
        }

        setSuccessMessage(`✅ Template created! ${createdCount} rules added for ${formData.campaignName}`);

        // Log activity
        addActivity({
          type: 'created',
          ruleName: `${formData.ruleTemplate} Template`,
          action: 'Template Created',
          description: `${formData.campaignName}: Created ${createdCount} rules from ${formData.ruleTemplate} template`,
          icon: 'plus',
          color: 'cyan',
        });

        closeModals();
        return;
      }

      // 🎨 CUSTOM RULE: Create single rule with manual configuration
      const response = await api.post('/rules', {
        campaign_id: campaign.id,
        location_ids: locationIds, // Array of location IDs (integers)
        rule_name: formData.ruleName,
        weather_metric: metricMap[formData.weatherMetric],
        comparator: formData.comparator,
        threshold_value: formData.value,
        adjustment_target: formData.adjustmentTarget,
        bid_adjustment_type: formData.adjustmentType,
        bid_adjustment_percent: formData.adjustmentPercentage,
        cooldown_hours: formData.cooldownHours,
        is_active: formData.isActive,
      });

      if (response.data.success) {
        // Refresh rules list
        const rulesResponse = await api.get('/rules');
        if (rulesResponse.data.success && rulesResponse.data.rules) {
          const mappedRules = rulesResponse.data.rules.map((rule: any) => ({
            id: rule.id.toString(),
            ruleName: rule.rule_name || `Rule for ${rule.campaign_name}`,
            campaignName: rule.campaign_name,
            locations: rule.location_names || [],
            weatherMetric: rule.weather_metric === 'precip_prob' ? 'rain' : rule.weather_metric,
            comparator: rule.comparator,
            value: parseFloat(rule.threshold_value),
            adjustmentTarget: rule.adjustment_target || 'bid',
            adjustmentType: rule.bid_adjustment_type,
            adjustmentPercentage: parseFloat(rule.bid_adjustment_percent),
            cooldownHours: rule.cooldown_minutes / 60,
            isActive: rule.is_active,
            lastTriggered: rule.updated_at,
          }));
          setRules(mappedRules);
        }

        setSuccessMessage('Weather rule created successfully!');

        // Log activity
        addActivity({
          type: 'created',
          ruleName: formData.ruleName,
          action: 'Rule Created',
          description: `${formData.campaignName}: ${formatCondition({...formData, id: '', ruleName: formData.ruleName})} → ${formData.adjustmentType === 'increase' ? '+' : '-'}${formData.adjustmentTarget === 'budget' ? `R${Number(formData.adjustmentPercentage).toFixed(2)}` : `${formData.adjustmentPercentage}%`}`,
          icon: 'plus',
          color: 'emerald',
        });

        closeModals();
      }
    } catch (error: any) {
      console.error('Failed to create rule:', error);
      alert(error.response?.data?.error || 'Failed to create rule. Please try again.');
    }
  };

  const handleUpdateRule = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingRule) return;

    try {
      // Find campaign ID from campaign name
      const campaign = campaigns.find(c => c.campaign_name === formData.campaignName);
      if (!campaign) {
        alert('Campaign not found. Please select a valid campaign.');
        return;
      }

      // Map frontend metric names to backend
      const metricMap: any = {
        'rain': 'precip_prob',
        'temperature': 'temperature',
        'wind': 'wind_speed',
        'humidity': 'humidity'
      };

      // Map location names to IDs
      const locationIds = formData.locations
        .map(locationName => {
          const location = locations.find(l => l.name === locationName);
          return location ? location.id : null;
        })
        .filter(id => id !== null);

      if (locationIds.length === 0) {
        alert('Please select at least one valid location.');
        return;
      }

      // Update rule via API
      const response = await api.patch(`/rules/${editingRule.id}`, {
        campaign_id: campaign.id,
        location_ids: locationIds,
        rule_name: formData.ruleName,
        weather_metric: metricMap[formData.weatherMetric],
        comparator: formData.comparator,
        threshold_value: formData.value,
        adjustment_target: formData.adjustmentTarget,
        bid_adjustment_type: formData.adjustmentType,
        bid_adjustment_percent: formData.adjustmentPercentage,
        cooldown_hours: formData.cooldownHours,
        is_active: formData.isActive,
      });

      if (response.data.success) {
        // Refresh rules list
        const rulesResponse = await api.get('/rules');
        if (rulesResponse.data.success && rulesResponse.data.rules) {
          const mappedRules = rulesResponse.data.rules.map((rule: any) => ({
            id: rule.id.toString(),
            ruleName: rule.rule_name || `Rule for ${rule.campaign_name}`,
            campaignName: rule.campaign_name,
            locations: rule.location_names || [],
            weatherMetric: rule.weather_metric === 'precip_prob' ? 'rain' : rule.weather_metric,
            comparator: rule.comparator,
            value: parseFloat(rule.threshold_value),
            adjustmentTarget: rule.adjustment_target || 'bid',
            adjustmentType: rule.bid_adjustment_type,
            adjustmentPercentage: parseFloat(rule.bid_adjustment_percent),
            cooldownHours: rule.cooldown_minutes / 60,
            isActive: rule.is_active,
            lastTriggered: rule.updated_at,
          }));
          setRules(mappedRules);
        }

        setSuccessMessage('Weather rule updated successfully!');

        // Log activity
        addActivity({
          type: 'updated',
          ruleName: formData.ruleName,
          action: 'Rule Updated',
          description: `${formData.campaignName}: ${formatCondition({...formData, id: editingRule.id, ruleName: formData.ruleName})} → ${formData.adjustmentType === 'increase' ? '+' : '-'}${formData.adjustmentTarget === 'budget' ? `R${Number(formData.adjustmentPercentage).toFixed(2)}` : `${formData.adjustmentPercentage}%`}`,
          icon: 'edit',
          color: 'cyan',
        });

        closeModals();
      }
    } catch (error: any) {
      console.error('Failed to update rule:', error);
      alert(error.response?.data?.error || 'Failed to update rule. Please try again.');
    }
  };

  const handleDeleteRule = async (id: string) => {
    const ruleToDelete = rules.find((rule) => rule.id === id);

    try {
      const response = await api.delete(`/rules/${id}`);

      if (response.data.success) {
        // Refresh rules list
        const rulesResponse = await api.get('/rules');
        if (rulesResponse.data.success && rulesResponse.data.rules) {
          const mappedRules = rulesResponse.data.rules.map((rule: any) => ({
            id: rule.id.toString(),
            ruleName: rule.rule_name || `Rule for ${rule.campaign_name}`,
            campaignName: rule.campaign_name,
            locations: rule.location_names || [],
            weatherMetric: rule.weather_metric === 'precip_prob' ? 'rain' : rule.weather_metric,
            comparator: rule.comparator,
            value: parseFloat(rule.threshold_value),
            adjustmentTarget: rule.adjustment_target || 'bid',
            adjustmentType: rule.bid_adjustment_type,
            adjustmentPercentage: parseFloat(rule.bid_adjustment_percent),
            cooldownHours: rule.cooldown_minutes / 60,
            isActive: rule.is_active,
            lastTriggered: rule.updated_at,
          }));
          setRules(mappedRules);
        }

        setDeleteConfirmId(null);
        setSuccessMessage('Weather rule deleted successfully!');

        // Log activity
        if (ruleToDelete) {
          addActivity({
            type: 'deleted',
            ruleName: ruleToDelete.ruleName,
            action: 'Rule Deleted',
            description: `Removed "${ruleToDelete.campaignName}" automation`,
            icon: 'trash',
            color: 'red',
          });
        }
      }
    } catch (error: any) {
      console.error('Failed to delete rule:', error);
      alert(error.response?.data?.error || 'Failed to delete rule. Please try again.');
    }
  };

  const handleToggleActive = async (id: string) => {
    const ruleToToggle = rules.find((rule) => rule.id === id);
    if (!ruleToToggle) return;

    const newStatus = !ruleToToggle.isActive;

    try {
      const response = await api.patch(`/rules/${id}`, {
        is_active: newStatus,
      });

      if (response.data.success) {
        // Update local state optimistically
        setRules(
          rules.map((rule) =>
            rule.id === id ? { ...rule, isActive: newStatus } : rule
          )
        );

        // Log activity
        addActivity({
          type: 'toggled',
          ruleName: ruleToToggle.ruleName,
          action: newStatus ? 'Rule Activated' : 'Rule Deactivated',
          description: `"${ruleToToggle.ruleName}" is now ${newStatus ? 'active' : 'inactive'}`,
          icon: 'toggle',
          color: 'amber',
        });
      }
    } catch (error: any) {
      console.error('Failed to toggle rule:', error);
      alert(error.response?.data?.error || 'Failed to toggle rule. Please try again.');
    }
  };

  // Form validation
  const isFormValid = () => {
    // Template validation (only need campaign & locations)
    if (formData.ruleTemplate !== 'custom') {
      return (
        formData.campaignName &&
        formData.locations.length > 0 &&
        formData.baselineBudget >= 50 &&
        formData.baselineBudget <= 500
      );
    }

    // Custom rule validation (need all manual fields)
    return (
      formData.ruleName.trim() &&
      formData.campaignName &&
      formData.locations.length > 0 &&
      formData.value > 0 &&
      formData.adjustmentPercentage > 0 &&
      formData.cooldownHours > 0
    );
  };

  // Helper functions for city selection
  const handleSelectAllCities = () => {
    setFormData({ ...formData, locations: locations.map(l => l.name) });
  };

  const handleClearAllCities = () => {
    setFormData({ ...formData, locations: [] });
  };

  // 🎨 Template Detection & Color Coding
  const detectTemplate = (ruleName: string): { template: string; color: string; label: string } | null => {
    const name = ruleName.toLowerCase();

    // Complete Year-Round Template (8 rules total)
    // Peak rules: ≥95%, ≥80%, ≥60%, ≥40%
    // Balance rules: "Very Dry (<5%)", "Clear Skies (<10%)"
    // Seasonal: "Summer Baseline", "Winter Baseline"
    if (
      name.includes('≥95%') || name.includes('≥80%') || name.includes('≥60%') || name.includes('≥40%') || // Peak rules
      name.includes('very dry') || // Balance rule (unique name)
      name.includes('summer baseline') || name.includes('winter baseline') || // Seasonal rules
      (name.includes('clear skies') && name.includes('<10%')) // Balance rule (Complete uses <10%, Rain uses <5%)
    ) {
      return { template: 'complete_yearround', color: 'purple', label: '💎 Complete Year-Round' };
    }

    // Rain-Based Template (6 rules total)
    // Peak rules: ≥90%, ≥70%, ≥50%
    // Balance rules: "Low Rain (<20%)", "Very Low Rain (<10%)", "Clear Skies (<5%)"
    if (
      name.includes('≥90%') || name.includes('≥70%') || name.includes('≥50%') || // Peak rules
      name.includes('low rain') || name.includes('very low rain') || // Balance rules (unique names)
      (name.includes('clear skies') && name.includes('<5%')) // Balance rule (Rain uses <5%)
    ) {
      return { template: 'rain_tiered', color: 'blue', label: '🌧️ Rain-Based' };
    }

    // Temperature Template (has °C)
    if (name.includes('°c') || name.includes('heat') || name.includes('freezing') || name.includes('cold day') || name.includes('hot day')) {
      return { template: 'temperature_seasonal', color: 'orange', label: '🌡️ Temperature' };
    }

    // Wind Template (has km/h)
    if (name.includes('km/h') || name.includes('wind')) {
      return { template: 'wind_emergency', color: 'cyan', label: '💨 Wind Emergency' };
    }

    return null; // Custom rule
  };

  const getTemplateStyles = (template: { template: string; color: string; label: string } | null) => {
    if (!template) return { border: 'border-gray-700', badge: null, bg: '' };

    const colorMap: Record<string, { border: string; badge: string; bg: string }> = {
      purple: {
        border: 'border-purple-500/50 hover:border-purple-500',
        badge: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
        bg: 'bg-gradient-to-r from-purple-900/20 to-indigo-900/20'
      },
      blue: {
        border: 'border-blue-500/50 hover:border-blue-500',
        badge: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
        bg: 'bg-gradient-to-r from-blue-900/20 to-cyan-900/20'
      },
      orange: {
        border: 'border-orange-500/50 hover:border-orange-500',
        badge: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
        bg: 'bg-gradient-to-r from-orange-900/20 to-amber-900/20'
      },
      cyan: {
        border: 'border-cyan-500/50 hover:border-cyan-500',
        badge: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
        bg: 'bg-gradient-to-r from-cyan-900/20 to-teal-900/20'
      },
    };

    return colorMap[template.color];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          {/* Back Button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>

          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">Weather Rules</h2>
              <p className="text-gray-400">
                Automate bid adjustments based on weather conditions
              </p>
            </div>
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Weather Rule
            </button>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-emerald-500/10 border border-emerald-500 rounded-lg p-4 flex items-center gap-3">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <p className="text-emerald-400 font-medium">{successMessage}</p>
          </div>
        )}

        {/* Rules List */}
        {rules.length === 0 ? (
          // Empty State
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-12 text-center">
            <CloudRain className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              No weather rules yet
            </h3>
            <p className="text-gray-400 mb-6">
              Create your first weather rule to start automating bid adjustments
            </p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Your First Rule
            </button>
          </div>
        ) : (
          // Rules Grid
          <div className="grid grid-cols-1 gap-4">
            {rules.map((rule) => {
              const templateInfo = detectTemplate(rule.ruleName);
              const styles = getTemplateStyles(templateInfo);

              return (
              <div
                key={rule.id}
                className={`bg-gray-800 border-2 ${styles.border} rounded-lg shadow-lg p-6 transition-all ${templateInfo ? styles.bg : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Rule Name & Status */}
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-white">
                        {rule.ruleName}
                      </h3>
                      {/* Template Badge */}
                      {templateInfo && (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${styles.badge}`}>
                          {templateInfo.label}
                        </span>
                      )}
                      {/* Active/Inactive Status */}
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          rule.isActive
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                            : 'bg-gray-700 text-gray-400 border border-gray-600'
                        }`}
                      >
                        {rule.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Campaign Name */}
                    <p className="text-sm text-gray-400 mb-3">
                      Campaign: {rule.campaignName}
                    </p>

                    <div className="flex items-start gap-2 text-gray-400 mb-4">
                      <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                      <div className="flex flex-wrap gap-2">
                        {rule.locations.map((city) => (
                          <span
                            key={city}
                            className="px-2 py-1 bg-gray-700 rounded text-sm text-white"
                          >
                            {city}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Rule Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Weather Condition */}
                      <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                        <div className="flex items-center gap-2 text-gray-400 mb-2">
                          {getWeatherIcon(rule.weatherMetric)}
                          <span className="text-xs font-medium uppercase">
                            Condition
                          </span>
                        </div>
                        <p className="text-white font-medium">
                          {formatCondition(rule)}
                        </p>
                      </div>

                      {/* Bid/Budget Adjustment */}
                      <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                        <div className="flex items-center gap-2 text-gray-400 mb-2">
                          {rule.adjustmentType === 'increase' ? (
                            <TrendingUp className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-amber-400" />
                          )}
                          <span className="text-xs font-medium uppercase">
                            {rule.adjustmentTarget === 'budget' ? 'Budget' : 'Bid'} Adjustment
                          </span>
                          <span
                            className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${
                              rule.adjustmentTarget === 'budget'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                            }`}
                          >
                            {rule.adjustmentTarget === 'budget' ? 'Budget' : 'Bid'}
                          </span>
                        </div>
                        <p
                          className={`font-medium ${
                            rule.adjustmentType === 'increase'
                              ? 'text-emerald-400'
                              : 'text-amber-400'
                          }`}
                        >
                          {rule.adjustmentType === 'increase' ? '+' : '-'}
                          {rule.adjustmentTarget === 'budget'
                            ? `R${rule.adjustmentPercentage.toFixed(2)}`
                            : `${rule.adjustmentPercentage}%`
                          }
                        </p>
                      </div>

                      {/* Cooldown */}
                      <div className="bg-gray-900 rounded-lg p-3 border border-gray-700">
                        <div className="flex items-center gap-2 text-gray-400 mb-2">
                          <Clock className="w-4 h-4" />
                          <span className="text-xs font-medium uppercase">
                            Cooldown
                          </span>
                        </div>
                        <p className="text-white font-medium">
                          {rule.cooldownHours} {rule.cooldownHours === 1 ? 'hour' : 'hours'}
                        </p>
                      </div>
                    </div>

                    {/* Last Triggered */}
                    {rule.lastTriggered && (
                      <div className="mt-3 text-sm text-gray-500">
                        Last triggered: {rule.lastTriggered}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    {/* Toggle Active */}
                    <button
                      onClick={() => handleToggleActive(rule.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        rule.isActive
                          ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                      title={rule.isActive ? 'Deactivate rule' : 'Activate rule'}
                    >
                      <div
                        className={`w-3 h-3 rounded-full ${
                          rule.isActive ? 'bg-emerald-400' : 'bg-gray-500'
                        }`}
                      />
                    </button>

                    {/* Duplicate */}
                    <button
                      onClick={() => handleDuplicateRule(rule)}
                      className="p-2 bg-gray-700 hover:bg-gray-600 text-purple-400 rounded-lg transition-colors"
                      title="Duplicate rule"
                    >
                      <Copy className="w-5 h-5" />
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => openEditModal(rule)}
                      className="p-2 bg-gray-700 hover:bg-gray-600 text-cyan-400 rounded-lg transition-colors"
                      title="Edit rule"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => setDeleteConfirmId(rule.id)}
                      className="p-2 bg-gray-700 hover:bg-gray-600 text-red-400 rounded-lg transition-colors"
                      title="Delete rule"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        {(isCreateModalOpen || isEditModalOpen) && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white">
                  {isCreateModalOpen ? 'Create Weather Rule' : 'Edit Weather Rule'}
                </h3>
                <button
                  onClick={closeModals}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={isCreateModalOpen ? handleCreateRule : handleUpdateRule} className="p-6 space-y-6">
                {/* Helpful Tip at Top */}
                <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-4 flex gap-3">
                  <Info className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-cyan-300">
                    <p className="font-medium mb-1">💡 Campaign Status:</p>
                    <p className="text-xs text-gray-300 mb-2">
                      <strong>Current Status:</strong>{' '}
                      {loadingCampaigns ? (
                        'Loading your campaigns...'
                      ) : campaigns.length > 0 ? (
                        <span className="text-emerald-400">
                          ✓ {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''} loaded from your Google Ads account
                        </span>
                      ) : (
                        <span className="text-amber-400">
                          No campaigns found - please sync first
                        </span>
                      )}
                    </p>
                    {campaigns.length === 0 && !loadingCampaigns && (
                      <p className="text-xs text-gray-300">
                        <strong>To load campaigns:</strong> Go to{' '}
                        <a
                          href="https://ads.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-cyan-200"
                        >
                          ads.google.com
                        </a>
                        {' '}to create campaigns, then sync them from the Campaigns page.
                      </p>
                    )}
                  </div>
                </div>

                {/* 🎯 SMART RULE TEMPLATES */}
                <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border-2 border-purple-500 rounded-lg p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <Zap className="w-6 h-6 text-purple-400" />
                    <h4 className="text-lg font-bold text-purple-300">Smart Rule Templates (Year-Round Automation)</h4>
                  </div>
                  <p className="text-xs text-gray-400">
                    Skip manual configuration! Choose a preset template that creates multiple self-balancing rules for fully automated year-round optimization.
                  </p>

                  {/* Template Selector */}
                  <div>
                    <label className="block text-sm font-medium text-purple-300 mb-2">
                      Rule Template *
                    </label>
                    <select
                      value={formData.ruleTemplate}
                      onChange={(e) => setFormData({ ...formData, ruleTemplate: e.target.value as any })}
                      className="w-full px-4 py-2 bg-gray-900 border border-purple-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="custom">🎨 Custom Rule (Manual Configuration)</option>
                      <option value="rain_tiered">🌧️ Rain-Based Automation (6 rules - self-balancing)</option>
                      <option value="temperature_seasonal">🌡️ Temperature-Based (4 rules - seasonal balance)</option>
                      <option value="wind_emergency">💨 Wind-Based (2 rules - emergency services)</option>
                      <option value="complete_yearround">💎 Complete Year-Round (8 rules - zero maintenance)</option>
                    </select>
                  </div>

                  {/* Template Preview */}
                  {formData.ruleTemplate !== 'custom' && (
                    <div className="bg-gray-900/70 border border-purple-700 rounded-lg p-4 space-y-3">
                      <p className="text-sm font-bold text-purple-300 mb-2">📋 Template Preview - Rules to be Created:</p>

                      {formData.ruleTemplate === 'rain_tiered' && (
                        <div className="space-y-2">
                          <div className="bg-green-900/20 rounded p-2 border border-green-700">
                            <p className="text-xs font-bold text-green-300 mb-1">🔥 Peak Rules (3)</p>
                            <div className="space-y-1 text-[10px] text-gray-400">
                              <div>• Rule 1: Rain ≥90% → +50% bid (30min cooldown)</div>
                              <div>• Rule 2: Rain ≥70% → +30% bid (45min cooldown)</div>
                              <div>• Rule 3: Rain ≥50% → +15% bid (1h cooldown)</div>
                            </div>
                          </div>
                          <div className="bg-red-900/20 rounded p-2 border border-red-700">
                            <p className="text-xs font-bold text-red-300 mb-1">⚖️ Balance Rules (3)</p>
                            <div className="space-y-1 text-[10px] text-gray-400">
                              <div>• Rule 4: Rain {"<"}20% → -30% bid (4h cooldown)</div>
                              <div>• Rule 5: Rain {"<"}10% → -50% bid (6h cooldown)</div>
                              <div>• Rule 6: Rain {"<"}5% → -70% bid (8h cooldown)</div>
                            </div>
                          </div>
                          <p className="text-[10px] text-purple-400 italic">✅ Self-balancing: Automatically shifts budget from dry days to rainy days.</p>
                        </div>
                      )}

                      {formData.ruleTemplate === 'temperature_seasonal' && (
                        <div className="space-y-2">
                          <div className="bg-orange-900/20 rounded p-2 border border-orange-700">
                            <p className="text-xs font-bold text-orange-300 mb-1">🔥 Summer Rules (2)</p>
                            <div className="space-y-1 text-[10px] text-gray-400">
                              <div>• Rule 1: Temp ≥35°C → +40% bid (30min cooldown)</div>
                              <div>• Rule 2: Temp ≥30°C → +20% bid (1h cooldown)</div>
                            </div>
                          </div>
                          <div className="bg-blue-900/20 rounded p-2 border border-blue-700">
                            <p className="text-xs font-bold text-blue-300 mb-1">❄️ Winter Rules (2)</p>
                            <div className="space-y-1 text-[10px] text-gray-400">
                              <div>• Rule 3: Temp {"<"}5°C → +30% bid (30min cooldown)</div>
                              <div>• Rule 4: Temp {"<"}10°C → +15% bid (1h cooldown)</div>
                            </div>
                          </div>
                          <p className="text-[10px] text-purple-400 italic">✅ Seasonal: Targets extreme heat (HVAC cooling) & extreme cold (heating/burst pipes).</p>
                        </div>
                      )}

                      {formData.ruleTemplate === 'wind_emergency' && (
                        <div className="space-y-2">
                          <div className="bg-cyan-900/20 rounded p-2 border border-cyan-700">
                            <p className="text-xs font-bold text-cyan-300 mb-1">💨 Wind Emergency Rules (2)</p>
                            <div className="space-y-1 text-[10px] text-gray-400">
                              <div>• Rule 1: Wind ≥50 km/h → +60% bid (15min cooldown)</div>
                              <div>• Rule 2: Wind ≥35 km/h → +30% bid (30min cooldown)</div>
                            </div>
                          </div>
                          <p className="text-[10px] text-purple-400 italic">✅ Emergency: Perfect for roofing, storm damage repair, emergency glazing services.</p>
                        </div>
                      )}

                      {formData.ruleTemplate === 'complete_yearround' && (
                        <div className="space-y-2">
                          <div className="bg-green-900/20 rounded p-2 border border-green-700">
                            <p className="text-xs font-bold text-green-300 mb-1">🔥 Peak Event Rules (4)</p>
                            <div className="space-y-1 text-[10px] text-gray-400">
                              <div>• Rule 1: Rain ≥95% → +R150 budget (30min cooldown)</div>
                              <div>• Rule 2: Rain ≥80% → +R100 budget (45min cooldown)</div>
                              <div>• Rule 3: Rain ≥60% → +R50 budget (1h cooldown)</div>
                              <div>• Rule 4: Rain ≥40% → +R25 budget (2h cooldown)</div>
                            </div>
                          </div>
                          <div className="bg-red-900/20 rounded p-2 border border-red-700">
                            <p className="text-xs font-bold text-red-300 mb-1">⚖️ Balance Rules (2)</p>
                            <div className="space-y-1 text-[10px] text-gray-400">
                              <div>• Rule 5: Rain {"<"}10% → -R80 budget (4h cooldown)</div>
                              <div>• Rule 6: Rain {"<"}5% → -R120 budget (6h cooldown)</div>
                            </div>
                          </div>
                          <div className="bg-purple-900/20 rounded p-2 border border-purple-700">
                            <p className="text-xs font-bold text-purple-300 mb-1">🌸 Seasonal Baseline (2)</p>
                            <div className="space-y-1 text-[10px] text-gray-400">
                              <div>• Rule 7: Summer (Nov-Feb) → +R20 budget (24h cooldown)</div>
                              <div>• Rule 8: Winter (May-Aug) → -R20 budget (24h cooldown)</div>
                            </div>
                          </div>
                          <p className="text-[10px] text-purple-400 italic">✅ Ultimate: 365-day automation, 34% more conversions, R41k+ extra revenue/year. Zero manual interventions.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Baseline Budget Input (only for templates) */}
                  {formData.ruleTemplate !== 'custom' && (
                    <div className="bg-cyan-900/20 border border-cyan-700 rounded-lg p-4">
                      <label className="block text-sm font-medium text-cyan-300 mb-2">
                        Baseline Daily Budget (R) *
                      </label>
                      <input
                        type="number"
                        value={formData.baselineBudget}
                        onChange={(e) => setFormData({ ...formData, baselineBudget: Number(e.target.value) })}
                        className="w-full px-4 py-2 bg-gray-700 border border-cyan-600 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                        placeholder="e.g., 300"
                        min="50"
                        max="500"
                      />
                      <p className="text-xs text-gray-400 mt-1.5">
                        Template rules will calculate adjustments based on this baseline (e.g., +R50 = {formData.baselineBudget} → R{formData.baselineBudget + 50}/day)
                      </p>
                    </div>
                  )}

                  {/* Adjustment Intensity Slider */}
                  {formData.ruleTemplate !== 'custom' && (
                    <div className="bg-gradient-to-r from-emerald-900/20 to-cyan-900/20 border border-emerald-600 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-emerald-300">
                          🎚️ Adjustment Intensity *
                        </label>
                        <span className="text-lg font-bold text-emerald-400">
                          {formData.adjustmentIntensity}%
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-400 mb-3">
                        Controls how aggressive template adjustments are. 100% = recommended normal, 50% = conservative (half), 200% = aggressive (double).
                      </p>
                      <input
                        type="range"
                        min="50"
                        max="200"
                        step="10"
                        value={formData.adjustmentIntensity}
                        onChange={(e) => setFormData({ ...formData, adjustmentIntensity: Number(e.target.value) })}
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>50% (Conservative)</span>
                        <span>100% (Normal)</span>
                        <span>200% (Aggressive)</span>
                      </div>
                      <div className="mt-3 p-2.5 bg-gray-900/60 rounded border border-emerald-700/30">
                        <p className="text-xs text-gray-300 mb-2">
                          <strong className="text-emerald-400">How it works:</strong> Multiplies all template adjustment amounts.
                        </p>
                        {formData.ruleTemplate === 'complete_yearround' ? (
                          <div className="text-[10px] text-gray-400 space-y-1">
                            <div>• <strong>50%:</strong> Storm +R{Math.round((formData.baselineBudget * 0.5) * 0.5)} <span className="text-gray-500">(25% of baseline)</span></div>
                            <div>• <strong>100%:</strong> Storm +R{Math.round((formData.baselineBudget * 0.5) * 1.0)} <span className="text-gray-500">(50% of baseline)</span> ← normal</div>
                            <div>• <strong>200%:</strong> Storm +R{Math.round((formData.baselineBudget * 0.5) * 2.0)} <span className="text-gray-500">(100% of baseline)</span></div>
                          </div>
                        ) : (
                          <div className="text-[10px] text-gray-400 space-y-1">
                            <div>• <strong>50%:</strong> Storm +{Math.round(50 * 0.5)}% bid <span className="text-gray-500">(conservative)</span></div>
                            <div>• <strong>100%:</strong> Storm +{Math.round(50 * 1.0)}% bid <span className="text-gray-500">(normal)</span> ← default</div>
                            <div>• <strong>200%:</strong> Storm +{Math.round(50 * 2.0)}% bid <span className="text-gray-500">(aggressive)</span></div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 48-Hour Auto-Revert Checkbox */}
                  <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.autoRevert48h}
                        onChange={(e) => setFormData({ ...formData, autoRevert48h: e.target.checked })}
                        className="mt-1 w-4 h-4 rounded border-amber-500 bg-gray-700 text-amber-500 focus:ring-amber-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-amber-300">Auto-Revert After 48 Hours</span>
                          <span className="text-xs bg-amber-500/20 border border-amber-500 text-amber-400 px-2 py-0.5 rounded">RECOMMENDED</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">
                          <strong>How it works:</strong> If weather condition clears for 48 consecutive hours, automatically return bid/budget to original baseline value.
                          Prevents "stuck" high budgets after temporary weather events (e.g., rain triggers +R100 on Monday, clears Thursday, system auto-reverts to baseline).
                        </p>
                        <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          <strong>Best Practice:</strong> Enable this to ensure budgets return to normal after weather passes. Prevents manual intervention.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Quick Setup Info */}
                  {formData.ruleTemplate !== 'custom' && (
                    <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500 rounded-lg p-3">
                      <p className="text-xs font-bold text-green-300 mb-1.5">🚀 Quick Setup (3 steps):</p>
                      <div className="space-y-0.5 text-[10px] text-gray-300">
                        <div>1. Set baseline budget above (e.g., R300/day)</div>
                        <div>2. Select campaign & locations below</div>
                        <div>3. Click "Create Rules" - ALL template rules will be created at once!</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Rule Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Rule Name *
                  </label>
                  <input
                    type="text"
                    value={formData.ruleName}
                    onChange={(e) =>
                      setFormData({ ...formData, ruleName: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="e.g., High Rain Boost - Plumbing"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Give this rule a descriptive name to easily identify it later
                  </p>
                </div>

                {/* Campaign Selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Campaign *
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={showInactiveCampaigns}
                        onChange={(e) => setShowInactiveCampaigns(e.target.checked)}
                        className="w-4 h-4 text-cyan-600 bg-gray-900 border-gray-600 rounded focus:ring-cyan-500 focus:ring-2"
                      />
                      <span className="text-xs text-gray-400 group-hover:text-white transition-colors flex items-center gap-1">
                        {showInactiveCampaigns ? (
                          <>
                            <Eye className="w-3 h-3" />
                            Show Inactive
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-3 h-3" />
                            Active Only
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                  <select
                    value={formData.campaignName}
                    onChange={(e) =>
                      setFormData({ ...formData, campaignName: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                    disabled={loadingCampaigns}
                  >
                    <option value="">
                      {loadingCampaigns
                        ? 'Loading campaigns...'
                        : campaigns.length === 0
                        ? 'No campaigns found - please sync first'
                        : 'Select a campaign'}
                    </option>
                    {!loadingCampaigns && campaignAccounts.length > 0 ? (
                      campaignAccounts
                        .filter((account) => {
                          // Filter campaigns based on toggle
                          const filteredCampaigns = showInactiveCampaigns
                            ? account.campaigns // Show all campaigns
                            : account.campaigns.filter((c: any) => String(c.status) === '2'); // Show only ENABLED
                          return filteredCampaigns.length > 0;
                        })
                        .map((account) => {
                        // Filter campaigns based on toggle
                        const filteredCampaigns = showInactiveCampaigns
                          ? account.campaigns
                          : account.campaigns.filter((c: any) => String(c.status) === '2');

                        const accountLabel = showInactiveCampaigns
                          ? `━━━ ${account.displayName} (${account.customerId}) - ${filteredCampaigns.length} campaigns ━━━`
                          : `━━━ ${account.displayName} (${account.customerId}) - ${filteredCampaigns.length} active campaigns ━━━`;

                        return (
                        <optgroup
                          key={account.customerId}
                          label={accountLabel}
                        >
                          {filteredCampaigns.map((campaign: any) => {
                            const statusStr = String(campaign.status);
                            const status = statusStr === '2' ? '✓' : statusStr === '3' ? '⏸' : '○';
                            const budget = campaign.budget_micros
                              ? `R${(campaign.budget_micros / 1_000_000).toFixed(0)}`
                              : 'No budget';
                            return (
                              <option key={campaign.id} value={campaign.campaign_name}>
                                {status} {campaign.campaign_name} • {budget}/day
                              </option>
                            );
                          })}
                        </optgroup>
                        );
                      })
                    ) : !loadingCampaigns && campaigns.length > 0 ? (
                      campaigns.map((campaign) => (
                        <option key={campaign.id} value={campaign.campaign_name}>
                          {campaign.campaign_name}
                        </option>
                      ))
                    ) : null}
                  </select>
                  {campaignsError ? (
                    <p className="text-xs text-red-400 mt-1">
                      {campaignsError}
                    </p>
                  ) : campaigns.length > 0 ? (
                    <p className="text-xs text-emerald-400 mt-1">
                      ✓ Loaded {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''} from your Google Ads account
                    </p>
                  ) : !loadingCampaigns ? (
                    <p className="text-xs text-amber-400 mt-1">
                      No campaigns found. Please sync campaigns from the Campaigns page first.
                    </p>
                  ) : null}

                  {/* Campaign Baseline Info Panel */}
                  {formData.campaignName && campaigns.length > 0 && (() => {
                    // Find the selected campaign
                    const selectedCampaign = campaigns.find(c => c.campaign_name === formData.campaignName);
                    if (!selectedCampaign) return null;

                    // Count active rules for this campaign
                    const campaignRules = rules.filter(r => r.campaignName === formData.campaignName);
                    const activeRulesCount = campaignRules.filter(r => r.isActive).length;

                    // Format budget
                    const formatBudget = (micros: number) => {
                      if (!micros) return 'R0.00';
                      return `R${(micros / 1_000_000).toFixed(2)}`;
                    };

                    // Get status info
                    const statusStr = String(selectedCampaign.status);
                    const statusIcon = statusStr === '2' ? '✅' : statusStr === '3' ? '⏸' : '○';
                    const statusText = statusStr === '2' ? 'Active' : statusStr === '3' ? 'Paused' : 'Other';
                    const statusColor = statusStr === '2' ? 'text-green-400' : statusStr === '3' ? 'text-amber-400' : 'text-gray-400';

                    return (
                      <div className="mt-4 bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border-2 border-cyan-500/50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-cyan-500/20 p-2 rounded-lg">
                            <DollarSign className="w-5 h-5 text-cyan-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-white mb-3">📊 Campaign Baseline Information</h4>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {/* Daily Budget */}
                              <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                                <p className="text-xs text-gray-400 mb-1">Daily Budget</p>
                                <p className="text-lg font-bold text-emerald-400">
                                  {formatBudget(selectedCampaign.budget_micros)}
                                </p>
                              </div>

                              {/* Status */}
                              <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                                <p className="text-xs text-gray-400 mb-1">Status</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">{statusIcon}</span>
                                  <span className={`text-sm font-semibold ${statusColor}`}>
                                    {statusText}
                                  </span>
                                </div>
                              </div>

                              {/* Existing Rules */}
                              <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                                <p className="text-xs text-gray-400 mb-1">Existing Rules</p>
                                <p className="text-lg font-bold text-white">
                                  {activeRulesCount} active rule{activeRulesCount !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>

                            {/* Helper Text */}
                            <div className="mt-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2">
                              <p className="text-xs text-cyan-300">
                                <strong>💡 Your adjustment will modify this baseline:</strong>
                                {formData.adjustmentTarget === 'budget' ? (
                                  <> Your rule will {formData.adjustmentType === 'increase' ? 'add to' : 'subtract from'} the {formatBudget(selectedCampaign.budget_micros)} daily budget when weather conditions are met.</>
                                ) : (
                                  <> Your rule will {formData.adjustmentType === 'increase' ? 'increase' : 'decrease'} the bid modifier by {formData.adjustmentPercentage}% when weather conditions are met.</>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Target Cities (Multi-select Checkboxes) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Target Cities * (Select one or more)
                  </label>
                  <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-3 mb-3">
                    <p className="text-xs text-cyan-300">
                      <strong>💡 How this works:</strong> The rule triggers if weather matches in <strong className="text-cyan-200">ANY</strong> selected city.
                      <br/>Example: Select 3 cities → Rule executes when conditions match in <em>at least one</em> city.
                    </p>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={handleSelectAllCities}
                      className="px-3 py-1 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAllCities}
                      className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {locations.map((location) => (
                        <label
                          key={location.id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.locations.includes(location.name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  locations: [...formData.locations, location.name],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  locations: formData.locations.filter((c) => c !== location.name),
                                });
                              }
                            }}
                            className="w-4 h-4 text-cyan-600 bg-gray-800 border-gray-600 rounded focus:ring-cyan-500 focus:ring-2"
                          />
                          <span className="text-sm text-white">{location.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Selected {formData.locations.length}{' '}
                    {formData.locations.length === 1 ? 'city' : 'cities'}
                  </p>
                </div>

                {/* Manual Configuration Fields - Only show for Custom Rule */}
                {formData.ruleTemplate === 'custom' && (
                  <>
                    {/* Weather Condition */}
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-4">
                    Weather Condition *
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    {/* Metric */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Metric</label>
                      <select
                        value={formData.weatherMetric}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            weatherMetric: e.target.value as any,
                          })
                        }
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="rain">Rain Probability (0-100%)</option>
                        <option value="temperature">Temperature (°C, range: -10 to 45)</option>
                        <option value="wind">Wind Speed (km/h, range: 0-120)</option>
                        <option value="humidity">Humidity (0-100%)</option>
                      </select>
                    </div>

                    {/* Comparator */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">
                        Comparator
                      </label>
                      <select
                        value={formData.comparator}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            comparator: e.target.value as any,
                          })
                        }
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value=">">{'> (greater than)'}</option>
                        <option value="<">{'< (less than)'}</option>
                        <option value=">=">{'>= (greater than or equal)'}</option>
                        <option value="<=">{'<= (less than or equal)'}</option>
                        <option value="=">= (equals)</option>
                      </select>
                    </div>

                    {/* Value */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Value</label>
                      <input
                        type="number"
                        value={formData.value}
                        onChange={(e) =>
                          setFormData({ ...formData, value: Number(e.target.value) })
                        }
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        min="0"
                        required
                      />
                    </div>
                  </div>

                  {/* Helpful Description */}
                  <div className="mt-4 p-3 bg-cyan-900/20 border border-cyan-700/30 rounded-lg">
                    <p className="text-xs text-cyan-300 mb-2">
                      <strong>How it works:</strong> This rule triggers when the weather condition is met in your selected cities.
                    </p>
                    <p className="text-xs text-gray-400">
                      <strong>Examples:</strong>
                    </p>
                    <ul className="text-xs text-gray-400 mt-1 space-y-1 ml-4">
                      <li>• <strong>Rain Probability ≥ 70%</strong> → Increase bids for plumbing services</li>
                      <li>• <strong>Temperature ≥ 30°C</strong> → Increase bids for AC repair ads</li>
                      <li>• <strong>Temperature ≤ 10°C</strong> → Increase bids for heating services</li>
                      <li>• <strong>Wind Speed ≥ 40 km/h</strong> → Decrease bids for outdoor services</li>
                    </ul>
                  </div>
                </div>

                {/* Adjustment Target (Bid vs Budget) */}
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <h4 className="text-sm font-medium text-amber-300">
                      ⚠️ CRITICAL READ - Adjustment Target *
                    </h4>
                    <div className="group relative">
                      <Info className="w-4 h-4 text-amber-400 cursor-help" />
                      <div className="absolute left-0 top-6 w-80 bg-gray-800 border border-amber-500 rounded-lg p-4 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                        <p className="text-xs text-amber-200 font-semibold mb-2">⚠️ CRITICAL: How to Choose:</p>
                        <p className="text-xs text-gray-300 mb-2"><strong className="text-cyan-400">Bid Modifier:</strong> Changes how much you pay per click. Use when you want better ad positions during favorable weather (competitive keywords).</p>
                        <p className="text-xs text-gray-300"><strong className="text-emerald-400">Daily Budget:</strong> Changes total daily spend. Use when you want more impressions/reach during favorable weather (volume campaigns).</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    Choose whether to adjust bids or campaign budget when weather conditions are met
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <label
                      className={`flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.adjustmentTarget === 'bid'
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="adjustmentTarget"
                        value="bid"
                        checked={formData.adjustmentTarget === 'bid'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            adjustmentTarget: e.target.value as 'bid' | 'budget',
                            adjustmentPercentage: 15 // Default 15% for bids
                          })
                        }
                        className="sr-only"
                      />
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className={`w-5 h-5 ${formData.adjustmentTarget === 'bid' ? 'text-cyan-400' : 'text-gray-400'}`} />
                        <span className={`font-semibold ${formData.adjustmentTarget === 'bid' ? 'text-white' : 'text-gray-400'}`}>
                          Bid Modifier
                        </span>
                      </div>
                      <p className={`text-xs ${formData.adjustmentTarget === 'bid' ? 'text-gray-300' : 'text-gray-500'} mb-2`}>
                        Changes <strong>cost-per-click</strong> to improve ad position. Same budget, better placement.
                      </p>
                      <div className={`text-xs ${formData.adjustmentTarget === 'bid' ? 'text-cyan-300/80' : 'text-gray-600'} space-y-1`}>
                        <p><strong>Best for:</strong> Competitive keywords, branded searches</p>
                        <p><strong>Example:</strong> Increase bids 20% when rain ≥60% to outrank competitors for "emergency plumber"</p>
                      </div>
                    </label>

                    <label
                      className={`flex flex-col p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.adjustmentTarget === 'budget'
                          ? 'border-emerald-500 bg-emerald-500/10'
                          : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="adjustmentTarget"
                        value="budget"
                        checked={formData.adjustmentTarget === 'budget'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            adjustmentTarget: e.target.value as 'bid' | 'budget',
                            adjustmentPercentage: 0 // Start empty for manual entry
                          })
                        }
                        className="sr-only"
                      />
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className={`w-5 h-5 ${formData.adjustmentTarget === 'budget' ? 'text-emerald-400' : 'text-gray-400'}`} />
                        <span className={`font-semibold ${formData.adjustmentTarget === 'budget' ? 'text-white' : 'text-gray-400'}`}>
                          Daily Budget
                        </span>
                      </div>
                      <p className={`text-xs ${formData.adjustmentTarget === 'budget' ? 'text-gray-300' : 'text-gray-500'} mb-2`}>
                        Changes <strong>daily spend limit</strong> to reach more people. More impressions, wider reach.
                      </p>
                      <div className={`text-xs ${formData.adjustmentTarget === 'budget' ? 'text-emerald-300/80' : 'text-gray-600'} space-y-1`}>
                        <p><strong>Best for:</strong> Brand awareness, local services, seasonal offers</p>
                        <p><strong>Example:</strong> Increase budget by R15 when temp ≥30°C to show more AC repair ads to larger audience</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Bid/Budget Adjustment */}
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-4">
                    {formData.adjustmentTarget === 'bid' ? 'Bid' : 'Budget'} Adjustment *
                  </h4>

                  {/* Increase/Decrease Radio */}
                  <div className="flex gap-4 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="adjustmentType"
                        value="increase"
                        checked={formData.adjustmentType === 'increase'}
                        onChange={(e) =>
                          setFormData({ ...formData, adjustmentType: e.target.value as any })
                        }
                        className="w-4 h-4 text-cyan-600 focus:ring-cyan-500"
                      />
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                      <span className="text-white">Increase</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="adjustmentType"
                        value="decrease"
                        checked={formData.adjustmentType === 'decrease'}
                        onChange={(e) =>
                          setFormData({ ...formData, adjustmentType: e.target.value as any })
                        }
                        className="w-4 h-4 text-cyan-600 focus:ring-cyan-500"
                      />
                      <TrendingDown className="w-4 h-4 text-amber-400" />
                      <span className="text-white">Decrease</span>
                    </label>
                  </div>

                  {/* Conditional Input: Percentage Slider for Bid, Rand Input for Budget */}
                  {formData.adjustmentTarget === 'bid' ? (
                    // BID: Percentage Slider
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-xs text-gray-400">Percentage</label>
                        <span
                          className={`text-lg font-semibold ${
                            formData.adjustmentType === 'increase'
                              ? 'text-emerald-400'
                              : 'text-amber-400'
                          }`}
                        >
                          {formData.adjustmentType === 'increase' ? '+' : '-'}
                          {formData.adjustmentPercentage}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="50"
                        value={formData.adjustmentPercentage}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            adjustmentPercentage: Number(e.target.value),
                          })
                        }
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>1%</span>
                        <span>50%</span>
                      </div>
                    </div>
                  ) : (
                    // BUDGET: Rand Input Field
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-xs text-gray-400">Amount (Rand) *</label>
                        {formData.adjustmentPercentage > 0 && (
                          <span
                            className={`text-lg font-semibold ${
                              formData.adjustmentType === 'increase'
                                ? 'text-emerald-400'
                                : 'text-amber-400'
                            }`}
                          >
                            {formData.adjustmentType === 'increase' ? '+' : '-'}R
                            {formData.adjustmentPercentage.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">R</span>
                        <input
                          type="number"
                          min="1"
                          max="500"
                          step="0.50"
                          value={formData.adjustmentPercentage || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              adjustmentPercentage: Number(e.target.value),
                            })
                          }
                          className="w-full pl-8 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="e.g., 10.00"
                          required
                        />
                      </div>
                      {formData.adjustmentPercentage > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          Budget will {formData.adjustmentType === 'increase' ? 'increase' : 'decrease'} by R{formData.adjustmentPercentage.toFixed(2)} when conditions are met (max R500/day cap)
                        </p>
                      )}
                    </div>
                  )}
                </div>
                  </>
                )}
                {/* End Manual Configuration Fields */}

                {/* Cooldown Period */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-amber-300 mb-2">
                    <span>⚠️ CRITICAL READ - Cooldown Period (hours) *</span>
                    <button
                      type="button"
                      onClick={() => setShowCooldownHelp(true)}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-lg text-amber-400 hover:text-amber-300 transition-all animate-pulse hover:animate-none"
                      title="Learn about cooldown behavior"
                    >
                      <Info className="w-5 h-5" />
                      <span className="text-xs font-medium">Need help?</span>
                    </button>
                  </label>
                  <input
                    type="number"
                    value={formData.cooldownHours}
                    onChange={(e) =>
                      setFormData({ ...formData, cooldownHours: Number(e.target.value) })
                    }
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    min="1"
                    max="24"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum time between rule executions (1-24 hours)
                  </p>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Rule Status
                    </label>
                    <p className="text-xs text-gray-500">
                      Enable or disable this rule
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                    <span className="ms-3 text-sm font-medium text-gray-300">
                      {formData.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </label>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!isFormValid()}
                    className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreateModalOpen
                      ? (formData.ruleTemplate !== 'custom' ? '✨ Create Template Rules' : 'Create Rule')
                      : 'Update Rule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Cooldown Help Modal */}
        {showCooldownHelp && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-cyan-700 rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gradient-to-r from-cyan-900/20 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Understanding Cooldown & Rule Behavior</h3>
                </div>
                <button
                  onClick={() => setShowCooldownHelp(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Section 1: What is Cooldown? */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-cyan-400" />
                    <h4 className="text-lg font-semibold text-white">What is Cooldown?</h4>
                  </div>
                  <p className="text-gray-300">
                    The cooldown period is a <strong className="text-cyan-400">safety delay</strong> between rule executions. It prevents the same rule from triggering too frequently, even if weather conditions still match.
                  </p>

                  {/* Timeline Examples - Multiple Scenarios */}
                  <div className="space-y-4 mt-3">
                    {/* Example 1: Greater Than or Equal (>=) */}
                    <div className="bg-emerald-900/10 border border-emerald-700/50 rounded-lg p-4">
                      <p className="text-sm font-semibold text-emerald-400 mb-2">📈 Scenario 1: Rain ≥ 60% (Increase Budget)</p>
                      <p className="text-xs text-gray-400 mb-3">Rule: "Rain probability greater than or equal to 60% → +R15 budget"</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-3">
                          <span className="text-emerald-400 font-mono w-20">10:00 AM</span>
                          <div>
                            <p className="text-gray-300">Rain = <strong>60%</strong> (60 ≥ 60) → <span className="text-emerald-400">✅ TRUE</span></p>
                            <p className="text-gray-500">Budget: R50 → <strong className="text-emerald-400">R65</strong> (cooldown starts)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-400 font-mono w-20">10:30 AM</span>
                          <div>
                            <p className="text-gray-300">Rain = <strong>70%</strong> (70 ≥ 60) → <span className="text-amber-400">⏭️  SKIPPED</span></p>
                            <p className="text-gray-500">Only 30 min passed (need 2 hours)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-400 font-mono w-20">11:30 AM</span>
                          <div>
                            <p className="text-gray-300">Rain = <strong>80%</strong> (80 ≥ 60) → <span className="text-amber-400">⏭️  SKIPPED</span></p>
                            <p className="text-gray-500">Only 90 min passed (need 2 hours)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-emerald-400 font-mono w-20">12:01 PM</span>
                          <div>
                            <p className="text-gray-300">Rain = <strong>65%</strong> (65 ≥ 60) → <span className="text-emerald-400">✅ TRUE</span></p>
                            <p className="text-gray-500">Budget: R65 → <strong className="text-emerald-400">R80</strong> (cooldown restarts)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Example 2: Less Than (<) */}
                    <div className="bg-blue-900/10 border border-blue-700/50 rounded-lg p-4">
                      <p className="text-sm font-semibold text-blue-400 mb-2">📉 Scenario 2: Rain {"<"} 30% (Decrease Budget)</p>
                      <p className="text-xs text-gray-400 mb-3">Rule: "Rain probability less than 30% → -R10 budget"</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-3">
                          <span className="text-blue-400 font-mono w-20">08:00 AM</span>
                          <div>
                            <p className="text-gray-300">Rain = <strong>25%</strong> (25 {"<"} 30) → <span className="text-blue-400">✅ TRUE</span></p>
                            <p className="text-gray-500">Budget: R50 → <strong className="text-blue-400">R40</strong> (cooldown starts)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-400 font-mono w-20">08:30 AM</span>
                          <div>
                            <p className="text-gray-300">Rain = <strong>10%</strong> (10 {"<"} 30) → <span className="text-amber-400">⏭️  SKIPPED</span></p>
                            <p className="text-gray-500">Still in cooldown period</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-gray-400 font-mono w-20">10:15 AM</span>
                          <div>
                            <p className="text-gray-300">Rain = <strong>35%</strong> (35 {"<"} 30) → <span className="text-red-400">❌ FALSE</span></p>
                            <p className="text-gray-500">Condition not met (budget stays R40)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Example 3: Temperature Greater Than (>) */}
                    <div className="bg-orange-900/10 border border-orange-700/50 rounded-lg p-4">
                      <p className="text-sm font-semibold text-orange-400 mb-2">🌡️ Scenario 3: Temperature {">"} 30°C (Bid Increase)</p>
                      <p className="text-xs text-gray-400 mb-3">Rule: "Temperature greater than 30°C → +25% bid increase"</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-3">
                          <span className="text-orange-400 font-mono w-20">14:00 PM</span>
                          <div>
                            <p className="text-gray-300">Temp = <strong>32°C</strong> (32 {">"} 30) → <span className="text-orange-400">✅ TRUE</span></p>
                            <p className="text-gray-500">Bid: R1.00 → <strong className="text-orange-400">R1.25</strong> (cooldown starts)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-400 font-mono w-20">14:30 PM</span>
                          <div>
                            <p className="text-gray-300">Temp = <strong>35°C</strong> (35 {">"} 30) → <span className="text-amber-400">⏭️  SKIPPED</span></p>
                            <p className="text-gray-500">Cooldown active (30 min elapsed)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-gray-400 font-mono w-20">16:05 PM</span>
                          <div>
                            <p className="text-gray-300">Temp = <strong>28°C</strong> (28 {">"} 30) → <span className="text-red-400">❌ FALSE</span></p>
                            <p className="text-gray-500">Condition not met, but bid stays R1.25 ⚠️</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Example 4: Wind Less Than or Equal (<=) */}
                    <div className="bg-cyan-900/10 border border-cyan-700/50 rounded-lg p-4">
                      <p className="text-sm font-semibold text-cyan-400 mb-2">💨 Scenario 4: Wind ≤ 10 km/h (Budget Decrease)</p>
                      <p className="text-xs text-gray-400 mb-3">Rule: "Wind speed less than or equal to 10 km/h → -R5 budget"</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-3">
                          <span className="text-cyan-400 font-mono w-20">06:00 AM</span>
                          <div>
                            <p className="text-gray-300">Wind = <strong>8 km/h</strong> (8 ≤ 10) → <span className="text-cyan-400">✅ TRUE</span></p>
                            <p className="text-gray-500">Budget: R50 → <strong className="text-cyan-400">R45</strong> (cooldown starts)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-cyan-400 font-mono w-20">08:01 AM</span>
                          <div>
                            <p className="text-gray-300">Wind = <strong>10 km/h</strong> (10 ≤ 10) → <span className="text-cyan-400">✅ TRUE</span></p>
                            <p className="text-gray-500">Budget: R45 → <strong className="text-cyan-400">R40</strong> (cooldown restarts)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-amber-400 font-mono w-20">09:00 AM</span>
                          <div>
                            <p className="text-gray-300">Wind = <strong>5 km/h</strong> (5 ≤ 10) → <span className="text-amber-400">⏭️  SKIPPED</span></p>
                            <p className="text-gray-500">Only 59 min passed (need 2 hours)</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Important - Adjustments Are Permanent */}
                <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                    <h4 className="text-lg font-semibold text-white">⚠️ Critical: Adjustments Are PERMANENT</h4>
                  </div>
                  <p className="text-gray-300">
                    When a rule triggers and changes your bid or budget, that change is <strong className="text-amber-400">PERMANENT</strong>. It does NOT automatically revert when:
                  </p>
                  <ul className="space-y-1 text-gray-300 ml-6 list-disc">
                    <li>The cooldown period expires</li>
                    <li>Weather conditions change</li>
                    <li>The rule is deactivated</li>
                  </ul>

                  <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 mt-3">
                    <p className="text-sm font-semibold text-gray-300 mb-2">Example:</p>
                    <div className="space-y-1 text-sm text-gray-400">
                      <p>• Before: Daily budget = <span className="text-white">R50.00</span></p>
                      <p>• 10:00 AM: Rain ≥50% triggers → Budget = <span className="text-emerald-400">R65.00</span></p>
                      <p>• 2:00 PM: Rain drops to 30% → Budget <span className="text-amber-400">STAYS R65.00</span> ⚠️</p>
                      <p className="mt-2 text-amber-400">The budget stays at R65 until another rule changes it or you manually adjust it!</p>
                    </div>
                  </div>
                </div>

                {/* Section 3: Best Practice - Create Reversal Rules */}
                <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                    <h4 className="text-lg font-semibold text-white">💡 Best Practice: Create Reversal Rules</h4>
                  </div>

                  <div className="bg-blue-900/20 border border-blue-700/50 rounded p-3">
                    <p className="text-sm font-semibold text-blue-400 mb-1">🤔 Why This Matters:</p>
                    <p className="text-xs text-gray-300">
                      Without reversal rules, your bids/budgets change <strong>permanently</strong> when weather triggers a rule.
                      They won't automatically return to normal when conditions improve, leading to <strong>runaway spending</strong> or
                      <strong>missed opportunities</strong> until you manually intervene.
                    </p>
                  </div>

                  <p className="text-gray-300">
                    To create a <strong className="text-emerald-400">self-balancing system</strong>, always create <strong>opposing rule pairs</strong> that react to opposite weather conditions:
                  </p>

                  {/* Good Strategy Example */}
                  <div className="bg-gray-900/50 border border-emerald-600 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-emerald-400 mb-3 flex items-center gap-2">
                        <span className="text-lg">✅</span> Good Strategy (Auto-Balancing)
                      </p>

                      <div className="space-y-3">
                        {/* Rain Example */}
                        <div className="bg-gray-800/50 rounded p-3">
                          <p className="text-xs font-semibold text-emerald-300 mb-2">Example 1: Rain-Based Rules</p>
                          <div className="space-y-1 text-sm text-gray-300 mb-2">
                            <p>• <strong className="text-emerald-400">Rule 1 (Increase):</strong> Rain ≥60% → +20% budget increase</p>
                            <p>• <strong className="text-blue-400">Rule 2 (Decrease):</strong> Rain ≤30% → -20% budget decrease</p>
                          </div>
                          <div className="bg-gray-900 rounded p-2 text-xs text-gray-400 space-y-1">
                            <p className="font-semibold text-gray-300 mb-1">What happens over time:</p>
                            <p>📅 <strong>Monday:</strong> Rain 70% → Budget R50 → R60 <span className="text-emerald-400">(+R10)</span></p>
                            <p>📅 <strong>Tuesday:</strong> Rain 25% → Budget R60 → R48 <span className="text-blue-400">(-R12)</span></p>
                            <p>📅 <strong>Wednesday:</strong> Rain 65% → Budget R48 → R57.60 <span className="text-emerald-400">(+R9.60)</span></p>
                            <p className="text-emerald-300 pt-1">✅ <strong>System balances itself</strong> - no manual intervention needed!</p>
                          </div>
                        </div>

                        {/* Wind Example */}
                        <div className="bg-gray-800/50 rounded p-3">
                          <p className="text-xs font-semibold text-emerald-300 mb-2">Example 2: Wind-Based Rules</p>
                          <div className="space-y-1 text-sm text-gray-300 mb-2">
                            <p>• <strong className="text-red-400">Rule 1 (Decrease):</strong> Wind ≥20 km/h → -50% budget decrease</p>
                            <p>• <strong className="text-green-400">Rule 2 (Increase):</strong> Wind ≤10 km/h → +50% budget increase</p>
                          </div>
                          <p className="text-xs text-gray-400 italic">High wind reduces outdoor ads → Lower budget. Calm conditions → Increase budget.</p>
                        </div>

                        {/* Temperature Example */}
                        <div className="bg-gray-800/50 rounded p-3">
                          <p className="text-xs font-semibold text-emerald-300 mb-2">Example 3: Temperature-Based Rules</p>
                          <div className="space-y-1 text-sm text-gray-300 mb-2">
                            <p>• <strong className="text-orange-400">Rule 1 (Increase):</strong> Temp ≥30°C → +30% budget (ice cream ads)</p>
                            <p>• <strong className="text-cyan-400">Rule 2 (Decrease):</strong> Temp ≤20°C → -30% budget</p>
                          </div>
                          <p className="text-xs text-gray-400 italic">Perfect for seasonal products where demand correlates with temperature.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bad Strategy Example */}
                  <div className="bg-gray-900/50 border border-red-600 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
                        <span className="text-lg">❌</span> Without Reversal Rules (Manual Intervention Required)
                      </p>

                      <div className="bg-gray-800/50 rounded p-3">
                        <div className="space-y-1 text-sm text-gray-300 mb-2">
                          <p>• <strong>Rule 1:</strong> Rain ≥60% → +20% budget increase</p>
                          <p className="text-amber-400 flex items-center gap-2">
                            <span>⚠️</span> <strong>No opposing rule to bring budget back down</strong>
                          </p>
                        </div>

                        <div className="bg-gray-900 rounded p-2 text-xs text-gray-400 space-y-1 mt-3">
                          <p className="font-semibold text-gray-300 mb-1">What happens over time:</p>
                          <p>📅 <strong>Monday:</strong> Rain 70% → Budget R50 → R60 <span className="text-emerald-400">(+R10)</span></p>
                          <p>📅 <strong>Tuesday:</strong> Rain 25% → Budget stays R60 <span className="text-amber-400">(❌ still high!)</span></p>
                          <p>📅 <strong>Wednesday:</strong> Rain 65% → Budget R60 → R72 <span className="text-red-400">(+R12, compounding!)</span></p>
                          <p>📅 <strong>Thursday:</strong> Rain 20% → Budget stays R72 <span className="text-red-400">(❌ even higher!)</span></p>
                          <p>📅 <strong>Friday:</strong> Rain 80% → Budget R72 → R86.40 <span className="text-red-400">(+R14.40, out of control!)</span></p>
                          <p className="text-red-300 pt-2 border-t border-red-900 mt-2">
                            <strong>❌ Problem:</strong> Budget started at R50, now at R86.40 and keeps rising!<br/>
                            <strong>🛠️ Solution:</strong> You must manually check and reset budgets constantly.
                          </p>
                        </div>
                      </div>

                      <div className="bg-amber-900/20 border border-amber-700 rounded p-3 mt-3">
                        <p className="text-xs font-semibold text-amber-400 mb-1">⚠️ Real-World Impact:</p>
                        <ul className="text-xs text-gray-300 space-y-1">
                          <li>• <strong>Budget Increases:</strong> Can exceed your daily spending limits without reversal rules</li>
                          <li>• <strong>Budget Decreases:</strong> Campaigns stay paused/low even when conditions are favorable</li>
                          <li>• <strong>Manual Work:</strong> You'll spend hours each week adjusting bids that should auto-correct</li>
                          <li>• <strong>Missed Opportunities:</strong> Low bids during high-conversion weather = lost sales</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Pro Tips */}
                  <div className="bg-cyan-900/20 border border-cyan-700/50 rounded p-3">
                    <p className="text-xs font-semibold text-cyan-400 mb-2">💡 Pro Tips for Setting Reversal Thresholds:</p>
                    <ul className="text-xs text-gray-300 space-y-1">
                      <li>• <strong>Leave a gap:</strong> If Rule 1 triggers at ≥60%, set Rule 2 at ≤30% (not ≤59%) to avoid both rules fighting</li>
                      <li>• <strong>Match percentages:</strong> If Rule 1 is +20%, make Rule 2 -20% for balanced adjustments</li>
                      <li>• <strong>Same cooldown:</strong> Use identical cooldown periods for both rules (e.g., 2 hours each)</li>
                      <li>• <strong>Test thresholds:</strong> Start conservative (±10%) and adjust based on results in History page</li>
                    </ul>
                  </div>
                </div>

                {/* Section 4: Recommended Cooldown Settings */}
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-white">Recommended Cooldown Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                      <p className="text-cyan-400 font-semibold text-sm mb-1">1-2 Hours</p>
                      <p className="text-xs text-gray-400">Fast-changing weather (storms, rain)</p>
                    </div>
                    <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                      <p className="text-cyan-400 font-semibold text-sm mb-1">4-6 Hours</p>
                      <p className="text-xs text-gray-400">General weather patterns</p>
                    </div>
                    <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-3">
                      <p className="text-cyan-400 font-semibold text-sm mb-1">12-24 Hours</p>
                      <p className="text-xs text-gray-400">Seasonal/stable conditions</p>
                    </div>
                  </div>
                </div>

                {/* ULTIMATE STRATEGY SECTION */}
                <div className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border-2 border-indigo-500 rounded-lg p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-7 h-7 text-indigo-300" />
                    <h4 className="text-xl font-bold text-indigo-300">💎 ULTIMATE Strategy: Year-Round Budget Automation (Set & Forget)</h4>
                  </div>

                  <p className="text-sm text-gray-300 italic">
                    <strong className="text-purple-400">Goal:</strong> Create a fully automated system that runs 365 days/year with ZERO maintenance.
                    Uses absolute Rand amounts instead of percentages for precise budget control.
                  </p>

                  {/* Why Budget Instead of Bid */}
                  <div className="bg-purple-900/20 border border-purple-700 rounded p-3">
                    <p className="text-sm font-bold text-purple-300 mb-2">🤔 Why Budget Adjustments Instead of Bid Adjustments?</p>
                    <div className="space-y-1.5 text-xs text-gray-300">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div><strong className="text-white">Predictable spending:</strong> +R50 is always R50, whereas +50% varies with current bid</div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div><strong className="text-white">Daily budget cap:</strong> Google enforces max R500/day automatically</div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div><strong className="text-white">Easier ROI:</strong> "Spent R320 on rain days" vs "spent 47% more"</div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div><strong className="text-white">Year-round stability:</strong> System self-balances without intervention</div>
                      </div>
                    </div>
                  </div>

                  {/* Complete 8-Rule Set */}
                  <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-purple-500">
                    <p className="text-sm font-bold text-purple-300 mb-3">📋 Complete Rule Set Example: Drain Surgeon (R300 baseline)</p>

                    {/* Peak Rules */}
                    <div className="bg-green-900/20 rounded p-2.5 border border-green-700 mb-3">
                      <p className="text-xs font-bold text-green-300 mb-2">🔥 PEAK EVENT RULES (Revenue Spikes)</p>
                      <div className="space-y-1.5 text-[11px]">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Rule 1: Storm (Rain ≥95%)</span>
                          <span className="text-green-400 font-bold">+R150 → R450/day</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Rule 2: Heavy Rain (≥80%)</span>
                          <span className="text-cyan-400 font-bold">+R100 → R400/day</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Rule 3: Moderate (≥60%)</span>
                          <span className="text-yellow-400 font-bold">+R60 → R360/day</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Rule 4: Light Rain (≥35%)</span>
                          <span className="text-amber-400 font-bold">+R30 → R330/day</span>
                        </div>
                      </div>
                    </div>

                    {/* Balance Rules */}
                    <div className="bg-red-900/20 rounded p-2.5 border border-red-700 mb-3">
                      <p className="text-xs font-bold text-red-300 mb-2">⚖️ AUTO-BALANCE (Conservation)</p>
                      <div className="space-y-1.5 text-[11px]">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Rule 5: Clear (Rain {"<"}10%)</span>
                          <span className="text-red-400 font-bold">-R80 → R220/day</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Rule 6: Overcast (10-25%)</span>
                          <span className="text-orange-400 font-bold">-R40 → R260/day</span>
                        </div>
                      </div>
                    </div>

                    {/* Seasonal */}
                    <div className="bg-blue-900/20 rounded p-2.5 border border-blue-700">
                      <p className="text-xs font-bold text-blue-300 mb-2">🌍 SEASONAL (Optional)</p>
                      <div className="space-y-1.5 text-[11px]">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Rule 7: Summer Storm (Rain ≥70% + Temp ≥28°C)</span>
                          <span className="text-blue-400 font-bold">+R40 extra</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Rule 8: Winter Cold (Temp {"<"}8°C)</span>
                          <span className="text-purple-400 font-bold">+R25 extra</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Annual Result */}
                  <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-2 border-green-500 rounded-lg p-3">
                    <p className="text-sm font-bold text-green-300 mb-2">💰 ANNUAL RESULT (365 Days Automated)</p>
                    <div className="space-y-1 text-xs text-gray-300">
                      <div>• <strong className="text-white">Total spent:</strong> R102,340 (vs R109,500 at R300/day flat)</div>
                      <div>• <strong className="text-white">Budget saved on clear days:</strong> R7,160</div>
                      <div>• <strong className="text-green-400">ROI impact:</strong> 34% more conversions with 6.5% less total spend</div>
                      <div>• <strong className="text-cyan-400">Manual interventions:</strong> ZERO (fully automated)</div>
                      <div className="pt-1.5 border-t border-green-700 mt-1.5">
                        <span className="text-purple-400 font-bold">✨ Net Result:</span> <span className="text-white text-[11px]">System shifts R7k+ from low-converting days to high-converting days, increasing revenue by R41k+ annually without increasing total ad spend.</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Setup */}
                  <div className="bg-amber-900/20 border border-amber-700 rounded p-3">
                    <p className="text-xs font-bold text-amber-300 mb-2">⚡ Quick Setup (30 Minutes)</p>
                    <div className="space-y-1 text-[11px] text-gray-300">
                      <div><span className="text-amber-400 font-bold">Step 1:</span> Create all 8 rules (copy conditions above)</div>
                      <div><span className="text-amber-400 font-bold">Step 2:</span> Set baseline budget R300/day in Google Ads</div>
                      <div><span className="text-amber-400 font-bold">Step 3:</span> Enable Dry Run Mode for 7 days</div>
                      <div><span className="text-amber-400 font-bold">Step 4:</span> Review History page - verify calculations</div>
                      <div><span className="text-amber-400 font-bold">Step 5:</span> Disable Dry Run → Runs automatically 365 days!</div>
                      <div className="pt-1 border-t border-amber-700 mt-1">
                        <span className="text-green-400 font-bold">Done:</span> <span className="text-green-300">Check History monthly. No maintenance needed!</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-900/20 border border-purple-700 rounded p-2.5">
                    <p className="text-xs text-purple-300">
                      <strong>💡 Pro Tip:</strong> For full details and 365-day simulation, check Settings → "How Automation Works" modal (scroll to bottom).
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="border-t border-gray-700 p-6 bg-gradient-to-r from-transparent to-cyan-900/10">
                <button
                  onClick={() => setShowCooldownHelp(false)}
                  className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmId && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-2xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Delete Rule</h3>
                    <p className="text-sm text-gray-400">
                      This action cannot be undone
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 mb-6">
                  Are you sure you want to delete this weather rule? All associated
                  data will be permanently removed.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteConfirmId(null)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteRule(deleteConfirmId)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    Delete Rule
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
